import { useState, useEffect, useRef, useCallback } from 'react';
import LoginScreen from './components/LoginScreen';
import SEO from '../../components/feature/SEO';

const API_BASE_URL = '/api/affiliates_contacts_api.asp';
const API_KEY = 'BM_9C8D4D38F6A84D2397E4978B5D1B0D6F';

const TIMEOUT_DURATION = 5 * 60 * 1000; // 5 minutes
const WARNING_BEFORE = 60 * 1000;        // warn at 1 minute remaining

interface Contact {
  uid: string;
  fullName: string;
  country: string | null;
  email: string;
  phone: string | null;
  subject: string;
  message: string;
  createdAt: string;
}

interface Affiliate {
  uid: string;
  type: string;
  contactName: string;
  company: string;
  email: string;
  phone: string | null;
  country: string;
  notes: string | null;
  createdAt: string;
}

interface ApiResponse<T> {
  ok: boolean;
  entity: string;
  action: string;
  count: number;
  data: T[];
}

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'contacts' | 'affiliates'>('contacts');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const addDebugLog = (message: string) => {
    const timestamp = new Date().toISOString();
    // Redact sensitive patterns before logging
    const redacted = message
      .replace(/Password=[^;"]*/gi, 'Password=***REDACTED***')
      .replace(/pwd=[^;"]*/gi, 'pwd=***REDACTED***')
      .replace(/User ID=[^;"]*/gi, 'User ID=***REDACTED***')
      .replace(/uid=[^;"]*/gi, 'uid=***REDACTED***')
      .replace(/(pk_live_|pk_test_|sk_live_|sk_test_)\w+/g, '$1***REDACTED***')
      .replace(/STRIPE_[A-Z_]+=[^\s]*/g, 'STRIPE_KEY=***REDACTED***');
    const logMessage = `[${timestamp}] ${redacted}`;
    console.log(logMessage);
    setDebugLogs(prev => [...prev, logMessage]);
  };

  const clearAllTimers = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  const handleLogout = useCallback(() => {
    clearAllTimers();
    setShowTimeoutWarning(false);
    setIsLoggedIn(false);
    sessionStorage.removeItem('admin_logged_in');
  }, []);

  const resetTimer = useCallback(() => {
    if (!isLoggedIn) return;
    clearAllTimers();
    setShowTimeoutWarning(false);

    warningRef.current = setTimeout(() => {
      setSecondsRemaining(60);
      setShowTimeoutWarning(true);
      countdownRef.current = setInterval(() => {
        setSecondsRemaining(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, TIMEOUT_DURATION - WARNING_BEFORE);

    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, TIMEOUT_DURATION);
  }, [isLoggedIn, handleLogout]);

  // Start/reset timer on login and on user activity
  useEffect(() => {
    if (!isLoggedIn) {
      clearAllTimers();
      return;
    }

    resetTimer();

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));

    return () => {
      clearAllTimers();
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [isLoggedIn, resetTimer]);

  useEffect(() => {
    if (isLoggedIn) {
      addDebugLog('🔵 Admin logged in, starting data fetch...');
      fetchData();
    }
  }, [isLoggedIn]);

  const fetchData = async () => {
    addDebugLog('🔵 fetchData() called');
    setLoading(true);
    setError(null);

    try {
      addDebugLog(`🔵 Window origin: ${window.location.origin}`);
      addDebugLog(`🔵 API Key: ${API_KEY.substring(0, 10)}...`);

      // Step 1: Initialize session via admin_session_start.asp
      addDebugLog('🔵 Initializing admin session via admin_session_start.asp...');
      const sessionInitUrl = `/api/admin_session_start.asp`;
      addDebugLog(`🔵 Calling: ${sessionInitUrl}`);
      
      const sessionResponse = await fetch(sessionInitUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-API-Key': API_KEY,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      addDebugLog(`🟢 Session init response status: ${sessionResponse.status}`);

      if (!sessionResponse.ok) {
        const errorText = await sessionResponse.text();
        addDebugLog(`🔴 Session init failed: ${errorText}`);
        throw new Error(`Failed to initialize admin session: ${sessionResponse.status} - ${errorText}`);
      }

      const sessionData = await sessionResponse.json();
      addDebugLog(`🟢 Session initialized successfully: ${JSON.stringify(sessionData)}`);
      
      if (sessionData.sessionId) {
        addDebugLog(`🟢 Session ID received: ${sessionData.sessionId}`);
      }

      // Step 2: Contacts
      const contactsUrl = `/api/affiliates_contacts_api.asp?entity=contacts&action=list`;
      addDebugLog(`🔵 Fetching contacts from: ${contactsUrl}`);

      const contactsResponse = await fetch(contactsUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-API-Key': API_KEY,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      addDebugLog(`🟢 Contacts response status: ${contactsResponse.status}`);

      if (!contactsResponse.ok) {
        const errorText = await contactsResponse.text();
        addDebugLog(`🔴 Contacts response error body: ${errorText}`);

        if (contactsResponse.status === 401) {
          throw new Error(
            `SESSION_REQUIRED: The API requires a valid website session. ` +
            `This usually means the API server needs you to be browsing the site first. ` +
            `Raw error: ${errorText}`
          );
        }

        throw new Error(`Contacts API failed: ${contactsResponse.status} - ${errorText}`);
      }

      const contactsText = await contactsResponse.text();

      // Detect connection string leak — never log raw credentials
      const isConnectionString = /Provider=|Data Source=|Initial Catalog=/i.test(contactsText);
      if (isConnectionString) {
        addDebugLog(`🔴 SECURITY: API returned a database connection string instead of JSON. The server-side ASP file has a bug — check affiliates_contacts_api.asp for a Response.Write that outputs the connection string variable. Credentials have been redacted from this log.`);
        throw new Error('SERVER BUG: The contacts API returned a raw database connection string instead of data. This is a server-side issue in affiliates_contacts_api.asp — it needs to be fixed on the server.');
      }

      addDebugLog(`🟢 Contacts raw response (first 500 chars): ${contactsText.substring(0, 500)}`);

      let contactsData: ApiResponse<Contact>;
      try {
        contactsData = JSON.parse(contactsText);
        addDebugLog(`🟢 Contacts JSON parsed successfully. Count: ${contactsData.count}`);
      } catch (parseError) {
        addDebugLog(`🔴 Failed to parse contacts JSON: ${parseError}`);
        throw new Error(`Invalid JSON from contacts API: ${parseError}`);
      }

      if (!contactsData.ok) {
        addDebugLog(`🔴 Contacts API returned ok=false`);
        throw new Error('Contacts API returned error status');
      }

      // Step 3: Affiliates
      const affiliatesUrl = `/api/affiliates_contacts_api.asp?entity=affiliates&action=list`;
      addDebugLog(`🔵 Fetching affiliates from: ${affiliatesUrl}`);

      const affiliatesResponse = await fetch(affiliatesUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-API-Key': API_KEY,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      addDebugLog(`🟢 Affiliates response status: ${affiliatesResponse.status}`);

      if (!affiliatesResponse.ok) {
        const errorText = await affiliatesResponse.text();
        addDebugLog(`🔴 Affiliates response error body: ${errorText}`);
        throw new Error(`Affiliates API failed: ${affiliatesResponse.status} - ${errorText}`);
      }

      const affiliatesText = await affiliatesResponse.text();

      // Detect connection string leak
      const isAffiliatesConnectionString = /Provider=|Data Source=|Initial Catalog=/i.test(affiliatesText);
      if (isAffiliatesConnectionString) {
        addDebugLog(`🔴 SECURITY: API returned a database connection string instead of JSON. The server-side ASP file has a bug — check affiliates_contacts_api.asp for a Response.Write that outputs the connection string variable. Credentials have been redacted from this log.`);
        throw new Error('SERVER BUG: The affiliates API returned a raw database connection string instead of data. This is a server-side issue in affiliates_contacts_api.asp — it needs to be fixed on the server.');
      }

      addDebugLog(`🟢 Affiliates raw response (first 500 chars): ${affiliatesText.substring(0, 500)}`);

      let affiliatesData: ApiResponse<Affiliate>;
      try {
        affiliatesData = JSON.parse(affiliatesText);
        addDebugLog(`🟢 Affiliates JSON parsed successfully. Count: ${affiliatesData.count}`);
      } catch (parseError) {
        addDebugLog(`🔴 Failed to parse affiliates JSON: ${parseError}`);
        throw new Error(`Invalid JSON from affiliates API: ${parseError}`);
      }

      if (!affiliatesData.ok) {
        addDebugLog(`🔴 Affiliates API returned ok=false`);
        throw new Error('Affiliates API returned error status');
      }

      addDebugLog(`🟢 Setting contacts state with ${contactsData.data.length} items`);
      setContacts(contactsData.data);

      addDebugLog(`🟢 Setting affiliates state with ${affiliatesData.data.length} items`);
      setAffiliates(affiliatesData.data);

      addDebugLog('✅ Data fetch completed successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      addDebugLog(`🔴 ERROR: ${errorMessage}`);
      addDebugLog(`🔴 Error stack: ${err instanceof Error ? err.stack : 'No stack trace'}`);
      setError(errorMessage);
      console.error('Admin dashboard error:', err);
    } finally {
      addDebugLog('🔵 Setting loading to false');
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortData = <T extends Contact | Affiliate>(data: T[]): T[] => {
    return [...data].sort((a, b) => {
      const aValue = a[sortField as keyof T];
      const bValue = b[sortField as keyof T];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const filterData = <T extends Contact | Affiliate>(data: T[]): T[] => {
    if (!searchTerm) return data;
    
    const term = searchTerm.toLowerCase();
    return data.filter(item => {
      const searchableFields = Object.values(item).filter(val => 
        typeof val === 'string'
      );
      return searchableFields.some(field => 
        field.toLowerCase().includes(term)
      );
    });
  };

  const getStats = () => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

    const contactsThisWeek = contacts.filter(c => new Date(c.createdAt) > weekAgo).length;
    const contactsThisMonth = contacts.filter(c => new Date(c.createdAt) > monthAgo).length;
    const contactsThisYear = contacts.filter(c => new Date(c.createdAt) > yearAgo).length;

    const affiliatesThisWeek = affiliates.filter(a => new Date(a.createdAt) > weekAgo).length;
    const affiliatesThisMonth = affiliates.filter(a => new Date(a.createdAt) > monthAgo).length;
    const affiliatesThisYear = affiliates.filter(a => new Date(a.createdAt) > yearAgo).length;

    return {
      contacts: {
        total: contacts.length,
        week: contactsThisWeek,
        month: contactsThisMonth,
        year: contactsThisYear,
      },
      affiliates: {
        total: affiliates.length,
        week: affiliatesThisWeek,
        month: affiliatesThisMonth,
        year: affiliatesThisYear,
      },
    };
  };

  if (!isLoggedIn) {
    return <LoginScreen onLoginSuccess={() => setIsLoggedIn(true)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-2xl w-full">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="text-center">
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Loading dashboard data...</h3>
              <p className="text-slate-600">Connecting to API server...</p>
            </div>
            
            {/* Debug Console */}
            <div className="w-full mt-6 bg-slate-900 rounded-lg p-4 max-h-96 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-mono text-teal-400">Debug Console</h4>
                <span className="text-xs text-slate-500">{debugLogs.length} logs</span>
              </div>
              <div className="space-y-1 font-mono text-xs">
                {debugLogs.length === 0 ? (
                  <div className="text-slate-500">Waiting for logs...</div>
                ) : (
                  debugLogs.map((log, index) => (
                    <div 
                      key={index} 
                      className={`${
                        log.includes('🔴') ? 'text-red-400' : 
                        log.includes('🟢') ? 'text-green-400' : 
                        'text-slate-300'
                      }`}
                    >
                      {log}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const isAccessDenied = error.includes('AccessDenied') || error.includes('Access Denied') || error.includes('403');
    const isSessionRequired = error.includes('SESSION_REQUIRED') || error.includes('401') || error.includes('Unauthorized');
    const isServerBug = error.includes('SERVER BUG') || error.includes('connection string');
    const isDbConnection = error.toLowerCase().includes('database connection failed') || error.toLowerCase().includes('database connection');

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-2xl w-full">
          <div className="flex flex-col items-center space-y-4">
            <div className={`w-16 h-16 ${isAccessDenied || isSessionRequired ? 'bg-amber-100' : isDbConnection ? 'bg-orange-100' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
              <i className={`ri-${isAccessDenied ? 'global-line text-amber-600' : isSessionRequired ? 'lock-line text-amber-600' : isDbConnection ? 'database-2-line text-orange-600' : 'error-warning-line text-red-600'} text-3xl`}></i>
            </div>
            <div className="text-center w-full">
              {isAccessDenied ? (
                <>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Preview Mode Limitation</h3>
                  <p className="text-slate-600 mb-2">
                    The data API is only accessible from your <strong>production domain</strong>.
                  </p>
                  <p className="text-slate-500 text-sm mb-4">
                    This admin dashboard will work fully when the site is deployed and accessed from its live URL.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-4">
                    <i className="ri-information-line mr-1"></i>
                    To test this page, visit it on your production domain.
                  </div>
                </>
              ) : isDbConnection ? (
                <>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Database Connection Failed</h3>
                  <p className="text-slate-600 mb-4">
                    The server reached <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">affiliates_contacts_api.asp</code> but it cannot connect to the SQL Server database.
                  </p>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-left mb-4">
                    <p className="font-semibold text-orange-800 mb-2"><i className="ri-database-2-line mr-1"></i>Things to check on your server:</p>
                    <ul className="list-disc list-inside space-y-1.5 text-orange-700 text-xs">
                      <li>SQL Server service is running — check Windows Services or SQL Server Configuration Manager</li>
                      <li>The database credentials in <code className="bg-orange-100 px-1 rounded">affiliates_contacts_api.asp</code> are correct (especially if you recently changed the password)</li>
                      <li>The database <code className="bg-orange-100 px-1 rounded">bioVerifier</code> exists on the SQL Server instance <code className="bg-orange-100 px-1 rounded">NS549028\MSSQLMASTER2017</code></li>
                      <li>The SQL Server user <code className="bg-orange-100 px-1 rounded">bioVerifier</code> has permission to access that database</li>
                      <li>The <code className="bg-orange-100 px-1 rounded">MSOLEDBSQL</code> OLE DB provider is installed on the IIS server</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-600 text-left mb-4">
                    <p className="font-semibold mb-1">Quick test — run this in SQL Server Management Studio:</p>
                    <code className="text-slate-700">SELECT 1 FROM bioVerifier.dbo.contacts</code>
                    <p className="mt-1 text-slate-500">If that errors, the DB or table doesn&apos;t exist / user lacks permission.</p>
                  </div>
                </>
              ) : isServerBug ? (
                <>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Server-Side Bug Detected</h3>
                  <p className="text-slate-600 mb-2">
                    The API is returning a <strong>database connection string</strong> instead of data.
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800 mb-4 text-left">
                    <p className="font-semibold mb-2"><i className="ri-error-warning-line mr-1"></i>What went wrong:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>File <code className="bg-red-100 px-1 rounded">affiliates_contacts_api.asp</code> on the server is outputting the DB connection string to the HTTP response</li>
                      <li>Look for a line like <code className="bg-red-100 px-1 rounded">Response.Write connStr</code> or similar near the top of that file</li>
                      <li>The connection string must only be used internally — never written to the response</li>
                    </ul>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-4 text-left">
                    <p className="font-semibold mb-1"><i className="ri-shield-keyhole-line mr-1"></i>Security Action Required:</p>
                    <p className="text-xs">Your database password was exposed in the API response. Please change your SQL Server password immediately in your database server settings.</p>
                  </div>
                </>
              ) : isSessionRequired ? (
                <>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Session Authentication Required</h3>
                  <p className="text-slate-600 mb-2">
                    The API server requires a valid website session before accepting requests.
                  </p>
                  <p className="text-slate-500 text-sm mb-4">
                    This typically means the backend API needs a session cookie that is set by the server when you first visit the site. This is a <strong>server-side session requirement</strong> — the API key alone is not sufficient.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-4 text-left">
                    <p className="font-semibold mb-1"><i className="ri-information-line mr-1"></i>What this means:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>The API at <code className="bg-amber-100 px-1 rounded">affiliates_contacts_api.asp</code> checks for a server-side session</li>
                      <li>The session is created by the ASP server when a user visits the site</li>
                      <li>The API key (<code className="bg-amber-100 px-1 rounded">X-API-Key</code>) is not enough on its own</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Error Loading Data</h3>
                  <p className="text-red-600 mb-4 text-sm">{error}</p>
                </>
              )}
              <button
                onClick={fetchData}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-2"></i>Retry
              </button>
            </div>

            {/* Debug Console */}
            <div className="w-full mt-6 bg-slate-900 rounded-lg p-4 max-h-96 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-mono text-teal-400">Debug Console</h4>
                <span className="text-xs text-slate-500">{debugLogs.length} logs</span>
              </div>
              <div className="space-y-1 font-mono text-xs">
                {debugLogs.map((log, index) => (
                  <div 
                    key={index} 
                    className={`${
                      log.includes('🔴') ? 'text-red-400' : 
                      log.includes('🟢') ? 'text-green-400' : 
                      log.includes('🟡') ? 'text-yellow-400' :
                      'text-slate-300'
                    }`}
                  >
                    {log}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = getStats();
  const filteredContacts = sortData(filterData(contacts));
  const filteredAffiliates = sortData(filterData(affiliates));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO title="XeroVerifier" description="XeroVerifier admin dashboard." noIndex />

      {/* Session Timeout Warning Modal */}
      {showTimeoutWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-3xl text-amber-600"></i>
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Session Expiring Soon</h2>
            <p className="text-slate-600 mb-4 text-sm">
              You will be automatically logged out due to inactivity in:
            </p>
            <div className="text-5xl font-bold text-amber-600 mb-6 tabular-nums">
              {secondsRemaining}s
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium whitespace-nowrap"
              >
                Logout Now
              </button>
              <button
                onClick={resetTimer}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap"
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Admin Dashboard</h1>
              <p className="text-sm text-slate-600 mt-1">Manage contacts and affiliate applications</p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Session timer indicator */}
              <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">
                <i className="ri-shield-check-line text-teal-500"></i>
                <span>Session active · 5 min timeout</span>
              </div>
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2 whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                <span>Refresh</span>
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors whitespace-nowrap"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Contacts Stats */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Contact Submissions</h3>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-mail-line text-2xl text-blue-600"></i>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-slate-800">{stats.contacts.total}</span>
                <span className="text-sm text-slate-600">Total</span>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-3 border-t border-slate-200">
                <div>
                  <div className="text-sm text-slate-600">This Week</div>
                  <div className="text-lg font-semibold text-slate-800">{stats.contacts.week}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600">This Month</div>
                  <div className="text-lg font-semibold text-slate-800">{stats.contacts.month}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600">This Year</div>
                  <div className="text-lg font-semibold text-slate-800">{stats.contacts.year}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Affiliates Stats */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Affiliate Applications</h3>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <i className="ri-team-line text-2xl text-teal-600"></i>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-slate-800">{stats.affiliates.total}</span>
                <span className="text-sm text-slate-600">Total</span>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-3 border-t border-slate-200">
                <div>
                  <div className="text-sm text-slate-600">This Week</div>
                  <div className="text-lg font-semibold text-slate-800">{stats.affiliates.week}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600">This Month</div>
                  <div className="text-lg font-semibold text-slate-800">{stats.affiliates.month}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600">This Year</div>
                  <div className="text-lg font-semibold text-slate-800">{stats.affiliates.year}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200">
            <div className="flex items-center justify-between p-6">
              <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('contacts')}
                  className={`px-6 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    activeTab === 'contacts'
                      ? 'bg-white text-teal-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  Contacts ({contacts.length})
                </button>
                <button
                  onClick={() => setActiveTab('affiliates')}
                  className={`px-6 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    activeTab === 'affiliates'
                      ? 'bg-white text-teal-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  Affiliates ({affiliates.length})
                </button>
              </div>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
              </div>
            </div>
          </div>

          {/* Contacts Table */}
          {activeTab === 'contacts' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th 
                      onClick={() => handleSort('fullName')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Name</span>
                        <i className={`ri-arrow-${sortField === 'fullName' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('email')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Email</span>
                        <i className={`ri-arrow-${sortField === 'email' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Phone</th>
                    <th 
                      onClick={() => handleSort('subject')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Subject</span>
                        <i className={`ri-arrow-${sortField === 'subject' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Message</th>
                    <th 
                      onClick={() => handleSort('country')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Country</span>
                        <i className={`ri-arrow-${sortField === 'country' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('createdAt')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Date</span>
                        <i className={`ri-arrow-${sortField === 'createdAt' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredContacts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                        No contacts found
                      </td>
                    </tr>
                  ) : (
                    filteredContacts.map((contact) => (
                      <tr key={contact.uid} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-800 whitespace-nowrap">{contact.fullName}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{contact.email}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{contact.phone || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{contact.subject}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">{contact.message}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{contact.country || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                          {new Date(contact.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Affiliates Table */}
          {activeTab === 'affiliates' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th 
                      onClick={() => handleSort('type')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Type</span>
                        <i className={`ri-arrow-${sortField === 'type' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('contactName')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Contact</span>
                        <i className={`ri-arrow-${sortField === 'contactName' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('company')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Company</span>
                        <i className={`ri-arrow-${sortField === 'company' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('email')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Email</span>
                        <i className={`ri-arrow-${sortField === 'email' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Phone</th>
                    <th 
                      onClick={() => handleSort('country')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Country</span>
                        <i className={`ri-arrow-${sortField === 'country' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Notes</th>
                    <th 
                      onClick={() => handleSort('createdAt')}
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Date</span>
                        <i className={`ri-arrow-${sortField === 'createdAt' && sortDirection === 'asc' ? 'up' : 'down'}-s-line text-sm`}></i>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredAffiliates.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                        No affiliates found
                      </td>
                    </tr>
                  ) : (
                    filteredAffiliates.map((affiliate) => (
                      <tr key={affiliate.uid} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            affiliate.type === 'Partner' ? 'bg-purple-100 text-purple-700' :
                            affiliate.type === 'Consultant' ? 'bg-blue-100 text-blue-700' :
                            affiliate.type === 'Accountant' ? 'bg-green-100 text-green-700' :
                            'bg-teal-100 text-teal-700'
                          }`}>
                            {affiliate.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-800 whitespace-nowrap">{affiliate.contactName}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{affiliate.company}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{affiliate.email}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{affiliate.phone || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{affiliate.country}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">{affiliate.notes || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                          {new Date(affiliate.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Debug Console (Always visible at bottom) */}
        <div className="mt-8 bg-slate-900 rounded-xl shadow-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-mono text-teal-400">Debug Console</h3>
            <div className="flex items-center space-x-4">
              <span className="text-xs text-slate-500">{debugLogs.length} logs</span>
              <button
                onClick={() => setDebugLogs([])}
                className="px-3 py-1 bg-slate-800 text-slate-300 rounded text-xs hover:bg-slate-700 transition-colors whitespace-nowrap"
              >
                Clear Logs
              </button>
            </div>
          </div>
          <div className="bg-black rounded-lg p-4 max-h-96 overflow-y-auto">
            <div className="space-y-1 font-mono text-xs">
              {debugLogs.length === 0 ? (
                <div className="text-slate-500">No logs yet...</div>
              ) : (
                debugLogs.map((log, index) => (
                  <div 
                    key={index} 
                    className={`${
                      log.includes('🔴') ? 'text-red-400' : 
                      log.includes('🟢') ? 'text-green-400' : 
                      log.includes('✅') ? 'text-teal-400 font-semibold' :
                      'text-slate-300'
                    }`}
                  >
                    {log}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
